<template>
  <component
    :is="tag"
    :id="buttonId"
    ref="button"
    :class="removeClassExtras(`
      ${component.size}
      ${component.state}
      ${full ? 'w-full justify-center' : ''}
      ${to ? 'inline-flex' : 'flex'}
      ${ticker
        ? `before:content-[''] before:w-[1.25rem] before:h-[1.25rem] before:border before:border-gray-400 before:rounded-full before:mr-2.5 before:relative enabled:hover:before:border-white enabled:hover:before:bg-[url('/icons/check.svg')] before:bg-no-repeat before:bg-center before:inline-block`
        : ''
    }
    `)"
    :data-testid="dataTestid"
    class="group text-dark empty:hidden items-center justify-center cursor-pointer"
    v-bind="attributes"
    @click="handleClick"
  >
    <slot v-if="$slots.default" />

    <IconsChevron
      v-if="hasIcon"
      ref="icon"
      :class="`${iconFillClasses} ${iconClass}`"
      :width="component.arrowSize.width"
      :height="component.arrowSize.height"
      :direction="iconDirection"
    />
  </component>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

const NuxtLink = resolveComponent('NuxtLink')

const { $tracking } = useNuxtApp()
const $attrs = useAttrs()
const $router = useRouter()

defineOptions({
  name: 'AtomsButton'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: 'atoms-btn'
  },
  size: {
    type: String,
    default: 'small',
    validator: value => ['extra small', 'small', 'medium', 'large'].includes(value)
  },

  type: {
    type: String,
    default: 'button',
    validator: value => ['button', 'submit'].includes(value)
  },

  theme: {
    type: String,
    default: 'primary',
    validator: value => ['primary', 'secondary', 'secondary2', 'secondaryBlue', 'secondaryRed', 'secondaryWithImage', 'tertiary', 'link'].includes(value)
  },

  hasIcon: {
    type: Boolean,
    default: false
  },

  iconDirection: {
    type: String,
    default: 'right',
    validator: value => ['left', 'right', 'top', 'bottom'].includes(value)
  },

  iconSize: {
    type: Object,
    default: () => ({})
  },

  iconClass: {
    type: String,
    default: ''
  },

  full: {
    type: Boolean,
    default: false
  },

  enableTracking: {
    type: Boolean,
    default: true
  },

  to: {
    type: [String, Object],
    default: ''
  },

  ticker: {
    type: Boolean,
    default: false
  },

  bgColor: {
    type: String,
    default: '',
    validator: value => ['', 'pink', 'blue'].includes(value)
  },

  disableTheme: {
    type: String,
    default: 'default',
    validator: value => ['default', 'light'].includes(value)
  },

  crossApp: {
    type: Boolean,
    default: false
  },

  trackingAdditionalParams: {
    type: Object,
    default: () => {
      return {}
    }
  },

  inverted: {
    type: Boolean,
    default: false
  }
})

const tag = computed(() => {
  const to = props.to

  if ((
    to &&
    typeof to === 'string' &&
    (
      to.includes('tel:') ||
      to.includes('mailto:') ||
      to.includes('http') ||
      to.startsWith('#') ||
      to.includes('www')
    )) ||
    props.crossApp
  ) {
    return 'a'
  }

  return to ? NuxtLink : 'button'
})

const attributes = computed(() => {
  const to = props.to

  const attrs = {
    ...((!_isEmpty($attrs) && {
      ...$attrs,
      type: props.type
    }) || {})
  }

  if (tag.value === 'a') {
    attrs.href = to

    delete attrs.type
  } else if (tag.value === NuxtLink) {
    /**
     * set `attrs.to` empty string because <nuxt-link> ignores @click handler
     * `attrs.to` value added in handleClick method
     * `attrs.target` value is added in handleClick method
     * `attrs.to deleted because to & href cannot be used together in <nuxt-link> component`
     */
    attrs.to = ''
    attrs.href = props.to
    attrs.target = ''

    delete attrs.type
    delete attrs.to
  } else if (tag.value === 'button') {
    attrs.type = props.type

    delete attrs.href
    delete attrs.target
    delete attrs.to
  }

  // added in CC-16479
  // delete attrs.class

  return attrs
})

const isDisabled = () => {
  return $attrs?.disabled === '' ||
    $attrs?.disabled ||
    $attrs?.['aria-disabled'] === '' ||
    $attrs?.['aria-disabled']
}

const component = computed(() => {
  const state = (() => {
    if (!props.theme) {
      return ''
    }

    const color = props.bgColor || props.theme

    const bgColor = {
      default: getKey(color, {
        default: 'bg-transparent',
        'primary, primary2': !props.inverted ? 'bg-semantic-primary-default text-semantic-surface-default' : 'bg-semantic-inverse-primary text-semantic-inverse-on-primary',
        pink: 'bg-semantic-primary-variant-light text-semantic-surface-default',
        secondaryBlue: 'bg-semantic-primary-variant-light text-semantic-surface-default',
        secondaryRed: 'bg-red-500 text-white'
      }),
      active: !isDisabled() && getKey(color, {
        default: 'active:bg-semantic-primary-variant-dark active:text-semantic-surface-default active:border-none',
        'primary, primary2': !props.inverted ? 'active:bg-semantic-primary-variant-dark active:text-semantic-surface-default active:border-none' : 'active:bg-semantic-inverse-primary-variant-light active:text-semantic-inverse-on-primary active:border-none active:shadow-md',
        secondary: !props.inverted ? 'active:bg-semantic-primary-variant-dark active:text-semantic-surface-default active:border-none' : 'active:bg-semantic-inverse-surface active:text-semantic-inverse-primary active:shadow-md',
        secondary2: 'active:bg-semantic-primary-variant-dark active:text-semantic-surface-default active:border-none',
        secondaryBlue: 'active:bg-semantic-primary-variant-dark active:text-semantic-surface-default active:border-none',
        secondaryRed: 'active:bg-red-500 active:text-white',
        link: 'active:text-semantic-surface-on-surface',
        tertiary: !props.inverted ? 'active:text-semantic-surface-on-surface' : 'active:text-semantic-inverse-primary-variant-light',
        pink: 'active:bg-semantic-primary-variant-light active:text-semantic-surface-default'
      })
    }

    const disabledColor = getKey(props.theme, {
      'primary, primary2': getKey(props.disableTheme, {
        default: 'disabled:shadow-none disabled:bg-semantic-surface-container disabled:text-semantic-surface-on-surface-variant',
        light: 'disabled:shadow-none disabled:bg-semantic-surface-container disabled:text-semantic-surface-on-surface-variant'
      }),
      'secondary, secondary2, secondaryBlue, secondaryRed': 'disabled:shadow-none disabled:bg-semantic-surface-container disabled:text-semantic-surface-on-surface-variant'
    })

    const common = `
      disabled:text-semantic-surface-on-surface-variant disabled:cursor
      ${bgColor.default}
      ${
        ['link', 'tertiary'].includes(props.theme)
          ? ''
          : (!isDisabled() && 'active:shadow-inner') || ''
      }
      ${disabledColor}
    `

    const map = {
      primary: !props.inverted ? `
        rounded-full text-semantic-surface-default border border-semantic-util-transparent
        hover:text-semantic-surface-default hover:bg-semantic-primary-variant-light hover:shadow-md
        ${!isDisabled() && 'hover:border hover:border-semantic-surface-default'}
        ${bgColor.active}
        font-bold
      ` : `
        rounded-full text-semantic-inverse-on-primary border border-semantic-util-transparent
        hover:text-semantic-inverse-on-primary hover:bg-semantic-inverse-primary-variant hover:shadow-md
        ${!isDisabled() && 'hover:border hover:border-semantic-inverse-on-primary'}
        ${bgColor.active}
        font-bold
      `,
      primary2: `
        rounded-full text-semantic-surface-default border border-semantic-util-transparent
        hover:text-semantic-surface-default hover:bg-semantic-primary-default hover:shadow-md
        ${bgColor.active}
        font-bold
      `,
      secondary: !props.inverted ? `
        border border-semantic-primary-default text-semantic-primary-default rounded-full bg-semantic-surface-default
        hover:border-semantic-surface-default hover:bg-semantic-primary-variant-light hover:text-semantic-surface-default hover:shadow-md
        ${bgColor.active}
        font-bold
      ` : `
        border border-semantic-inverse-primary text-semantic-inverse-primary rounded-full bg-transparent
        hover:border-semantic-inverse-primary hover:bg-semantic-inverse-container-lowest hover:text-semantic-inverse-primary hover:shadow-md
        ${bgColor.active}
        font-bold
      `,
      secondary2: `
        border border-semantic-primary-variant-light text-semantic-primary-variant-light rounded-full bg-semantic-surface-default
        hover:border-text-semantic-surface-default hover:bg-semantic-primary-default hover:text-semantic-surface-default hover:shadow-md
        ${bgColor.active} !font-regular hover:!font-bold
      `,
      secondaryBlue: `
        border border-semantic-primary-variant-light text-semantic-primary-variant-light rounded-full bg-semantic-surface-default
        hover:border-text-semantic-surface-default hover:bg-semantic-primary-default hover:text-semantic-surface-default hover:shadow-md
        ${bgColor.active} !font-regular hover:!font-bold
      `,
      secondaryRed: `
        rounded-full
        hover:text-white hover:bg-dark hover:shadow-md
        ${bgColor.active}
        font-bold
      `,
      secondaryWithImage: `
        border border-semantic-primary-variant-light text-semantic-primary-variant-light rounded-full bg-semantic-surface-default
        hover:border-text-semantic-surface-default hover:bg-semantic-primary-default hover:text-semantic-surface-default hover:shadow-md
        ${bgColor.active} !font-regular hover:!font-bold
        !p-4 flex-col
      `,
      tertiary: !props.inverted ? `
        ${props.hasIcon ? 'text-semantic-primary-default' : 'text-semantic-inverse-on-surface'}
        hover:text-semantic-primary-variant-light
        ${bgColor.active}
        font-bold
      ` : `
        text-semantic-inverse-primary
        hover:text-semantic-inverse-primary-variant
        ${bgColor.active}
        font-bold
      `,
      link: `
        text-semantic-primary-default hover:text-semantic-primary-variant-light
        ${bgColor.active}
        font-bold
      `
    }

    return removeClassExtras(map[props.theme] + ` ${common}`)
  })()

  const size = (() => {
    if (!props.size || !props.theme) {
      return ''
    }

    const map = {
      'extra small': 'text-xs',
      small: 'text-sm',
      medium: 'text-base',
      large: 'text-lg leading-[1.625rem]',
      'base-lg': 'text-base-font-size-lg'
    }

    const px = {
      default: ' px-8',
      'extra-small': ' px-6'
    }

    if (props.hasIcon) {
      px.default = ' pl-8 pr-6'
      px['extra-small'] = ' pl-6 pr-4'
    }

    if (props.theme !== 'tertiary') {
      map['extra small'] = ` py-[0.125rem] ${px['extra-small']}`
      map.small += ` py-[0.3125rem] ${px.default}`
      map.medium += ` py-2 ${px.default}`
      map.large += ` py-[0.6875rem] ${px.default}`
      map['base-lg'] += ` py-4 ${px.default}`
    } else {
      map[props.size] += ' p-2'
    }

    return removeClassExtras(map[props.size])
  })()

  const arrowSize = (() => {
    if (!props.size || !props.theme) {
      return ''
    }

    if (!_isEmpty(props.iconSize)) {
      return props.iconSize
    }

    const map = {
      'extra small': {
        height: '10',
        width: '14'
      },
      small: {
        height: '8',
        width: '13'
      },
      medium: {
        height: '10',
        width: '15'
      },
      large: {
        height: '13',
        width: '17'
      }
    }

    return map[props.size]
  })()

  return {
    state,
    size,
    arrowSize
  }
})

const iconFillClasses = computed(() => {
  const baseClasses = 'ml-3 group-disabled:fill-semantic-states-disabled-default'

  if (!props.inverted) {
    // Non-inverted fill classes based on theme
    switch (props.theme) {
      case 'primary':
        return `${baseClasses} fill-semantic-inverse-on-surface group-active:fill-semantic-inverse-on-surface group-hover:fill-semantic-inverse-on-surface`
      case 'secondary':
        return `${baseClasses} fill-semantic-primary-default group-active:fill-semantic-inverse-on-surface group-hover:fill-semantic-inverse-on-surface`
      case 'tertiary':
        return `${baseClasses} fill-semantic-primary-default group-active:fill-semantic-surface-on-surface group-hover:fill-semantic-primary-variant-light`
      default:
        return `${baseClasses} fill-semantic-inverse-on-surface group-active:fill-semantic-inverse-on-surface group-hover:fill-semantic-inverse-on-surface`
    }
  }

  // Inverted fill classes based on theme
  switch (props.theme) {
    case 'primary':
      return `${baseClasses} fill-semantic-inverse-on-primary group-active:fill-semantic-inverse-on-primary group-hover:fill-semantic-inverse-on-primary`
    case 'secondary':
      return `${baseClasses} fill-semantic-inverse-primary group-active:fill-semantic-inverse-primary group-hover:fill-semantic-inverse-primary`
    case 'tertiary':
      return `${baseClasses} fill-semantic-inverse-primary group-active:fill-semantic-inverse-primary-variant-light group-hover:fill-semantic-inverse-primary-variant`
    default:
      return `${baseClasses} fill-semantic-inverse-on-surface group-active:fill-semantic-inverse-on-surface group-hover:fill-semantic-inverse-on-surface`
  }
})

const buttonId = $attrs.id || `button_${useId()}`

const handleClick = event => {
  try {
    if (props.enableTracking) {
      $tracking.click(event, props.to, props.trackingAdditionalParams)
    }

    if (props.to && tag.value === NuxtLink) {
      if ($attrs.target) {
        const link = $router.resolve(props.to)
        openInBrowser(link.href, $attrs.target)
      } else {
        $router.push(props.to)
      }
    }
  } catch (error) {
    throw new Error(error?.message ?? String(error), { cause: error })
  }
}
</script>
